import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { NotFoundPanel } from '@/components/feedback/not-found-panel';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { CopyButton } from '@/components/ui/copy-button';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { MarketMakerEditor } from '@/features/mm-programs/market-maker-editor';
import { ProgramHistory } from '@/features/mm-programs/program-history';
import { MM_PROGRAMS_LIST_QUERY_KEY, mmProgramDowntimeQueryKey, mmProgramHistoryQueryKey, mmProgramQueryKey, useMmProgram } from '@/hooks/queries/mm-programs/use-mm-programs';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { getErrorMessage, isNotFoundError } from '@/lib/api/error';
import { MM_PROGRAMS_ROUTE, userConsolePath } from '@/lib/paths';
import type { GetMmProgramResponse } from '@/services/api/mm-programs';

const MmProgramDetailPage = () => {
  const queryClient = useQueryClient();
  const { pid = '' } = useParams<{ pid: string }>();
  const query = useMmProgram(pid);
  const loaded = query.data;

  useDocumentTitle(loaded ? `${loaded.marketMaker.name} - MM Programs` : undefined);

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <Link className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground" to={MM_PROGRAMS_ROUTE}>
        <ArrowLeft className="size-4" />
        Back to MM programs
      </Link>

      {query.isPending ? (
        <LoadingPanel className="py-24" />
      ) : query.isError && isNotFoundError(query.error) ? (
        <NotFoundPanel message={`No market maker settings exist for ${pid}.`} title="Market maker not found" />
      ) : query.isError ? (
        <QueryErrorBanner message={`Failed to fetch market maker: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} />
      ) : loaded ? (
        <>
          <header className="flex flex-col gap-2">
            <h1 className="text-2xl font-semibold">{loaded.marketMaker.name}</h1>
            <dl className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <div className="flex items-center gap-1.5">
                <dt className="text-muted-foreground">PID</dt>
                <dd className="flex items-center gap-1">
                  <Link className="font-mono text-xs underline-offset-2 hover:underline" to={userConsolePath(loaded.marketMaker.p)}>
                    {loaded.marketMaker.p}
                  </Link>
                  <CopyButton label="Copy PID" value={loaded.marketMaker.p} />
                </dd>
              </div>
              <div className="flex items-center gap-1.5">
                <dt className="text-muted-foreground">FAST ID</dt>
                <dd className="font-mono text-xs tabular-nums">{loaded.marketMaker.fast_id}</dd>
              </div>
              <div className="flex items-center gap-1.5">
                <dt className="text-muted-foreground">Version</dt>
                <dd className="tabular-nums">{loaded.version}</dd>
              </div>
            </dl>
          </header>
          <MarketMakerEditor
            canEdit={loaded.canEdit}
            key={`${pid}:${loaded.version}`}
            marketMaker={loaded.marketMaker}
            onReload={() => query.refetch()}
            onSaved={(response) => {
              queryClient.setQueryData<GetMmProgramResponse>(mmProgramQueryKey(pid), { marketMaker: response.marketMaker, version: response.version, canEdit: loaded.canEdit });
              void queryClient.invalidateQueries({ queryKey: MM_PROGRAMS_LIST_QUERY_KEY });
              void queryClient.invalidateQueries({ queryKey: mmProgramHistoryQueryKey(pid) });
              void queryClient.invalidateQueries({ queryKey: mmProgramDowntimeQueryKey(pid) });
            }}
            version={loaded.version}
          />
          <ProgramHistory pid={pid} />
        </>
      ) : null}
    </div>
  );
};

export default MmProgramDetailPage;
